import '../styles/globals.css'
import '../components/layout/wrapper/beamer.css'
import 'react-quill/dist/quill.snow.css' // required for 'ql-editor' class
import { datadogRum } from '@datadog/browser-rum'
import { StyledEngineProvider } from '@mui/material'
import { LicenseInfo } from '@mui/x-license'
import { AppProps } from 'next/app'
import Head from 'next/head'
import { useRouter } from 'next/router'
import { SessionProvider } from 'next-auth/react'
import { type ReactElement, type ReactNode, useEffect } from 'react'
import { analytics } from '@omnea-app/fe-analytics/client'
import { type NextPageWithLayout } from '@omnea-app/fe-app-shell/NextPageWithLayout'
import { NotificationProvider } from '@omnea-app/fe-app-shell/NotificationProvider'
import { Auth } from '@omnea-app/fe-auth/Auth'
import { TokenProvider } from '@omnea-app/fe-auth/TokenProvider'
import { useIsFullyAuthenticated } from '@omnea-app/fe-auth/UserSessionProvider'
import { usePreventNumberInputScroll } from '@omnea-app/fe-browser-utils/usePreventNumberInputScroll'
import { ConfigTranslationsProvider } from '@omnea-app/fe-i18n/ConfigTranslations'
import { TranslationsProvider } from '@omnea-app/fe-i18n/TranslationsContext'
import { FeatureFlagsProvider } from '@omnea-app/fe-org-settings/FeatureFlagsProvider'
import { LdFeatureFlagProvider } from '@omnea-app/fe-org-settings/LdFeatureFlagProvider'
import { AutoScrollToCommentProvider } from '@omnea-app/frontend/providers/AutoScrollToCommentProvider'
import { StatsProvider } from '@omnea-app/frontend/providers/StatsProvider'
import { useJsBundleInvalidation } from '@omnea-app/frontend/utils/hooks/useJsBundleInvalidation'
import { installDDFetchErrorCapture } from '@omnea-app/frontend/utils/observability/ddFetchErrorCapture'
import { ReactQueryProvider } from '@omnea-app/frontend/utils/react-query/ReactQueryProvider'
import { NewThemeProviderIfEnabled } from '@omnea-app/omui/NewThemeProvider'
import { SnackbarProvider } from '@omnea-app/omui/SnackbarProvider'
import { ThemeProvider } from '@omnea-app/omui/ThemeProvider'
import { PageTitleProvider } from '../components/layout/page-metadata/PageTitleContext'
import { AppWrapper } from '../components/layout/wrapper/AppWrapper'
import { BeamerProvider } from '../components/layout/wrapper/beamer'
import { PylonProvider } from '../components/layout/wrapper/pylon'
import UpTimePage from './uptime'

// eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- FIXME: bulk migration to add error
LicenseInfo.setLicenseKey(process.env.MUI_LICENSE_KEY!)

const NEXT_PUBLIC_DD_RUM_APPLICATION_ID = process.env.NEXT_PUBLIC_DD_RUM_APPLICATION_ID
const NEXT_PUBLIC_DD_RUM_CLIENT_TOKEN = process.env.NEXT_PUBLIC_DD_RUM_CLIENT_TOKEN
const NEXT_PUBLIC_DD_SITE = process.env.NEXT_PUBLIC_DD_SITE ?? 'datadoghq.eu'

if (NEXT_PUBLIC_DD_RUM_APPLICATION_ID && NEXT_PUBLIC_DD_RUM_CLIENT_TOKEN) {
  datadogRum.init({
    applicationId: NEXT_PUBLIC_DD_RUM_APPLICATION_ID,
    clientToken: NEXT_PUBLIC_DD_RUM_CLIENT_TOKEN,
    site: NEXT_PUBLIC_DD_SITE,
    service: 'omnea-app',
    env: process.env.OMNEA_APP_ENV,
    version: process.env.NEXT_PUBLIC_BUNDLE_ID ?? 'unknown',
    sessionSampleRate: 100,
    sessionReplaySampleRate: 100,
    traceSampleRate: 50,
    trackBfcacheViews: true,
    trackResources: true,
    defaultPrivacyLevel: 'allow',
    allowedTracingUrls: process.env.API_ROOT_URL ? [process.env.API_ROOT_URL] : [],
  })
  installDDFetchErrorCapture()
}

const AuthenticatedAppProviders = ({ children }: { children: ReactNode }) => {
  const isAuthenticated = useIsFullyAuthenticated()

  if (!isAuthenticated) {
    return <>{children}</>
  }

  return (
    <LdFeatureFlagProvider>
      <NewThemeProviderIfEnabled>
        <FeatureFlagsProvider>
          <ConfigTranslationsProvider>
            <PylonProvider>
              <BeamerProvider />
              <StatsProvider>
                <NotificationProvider>
                  <AutoScrollToCommentProvider>{children}</AutoScrollToCommentProvider>
                </NotificationProvider>
              </StatsProvider>
            </PylonProvider>
          </ConfigTranslationsProvider>
        </FeatureFlagsProvider>
      </NewThemeProviderIfEnabled>
    </LdFeatureFlagProvider>
  )
}

type AppPropsWithLayout = AppProps & {
  Component: NextPageWithLayout
}

const App = ({ Component, pageProps: { session: serverSession, ...pageProps }, ...otherProps }: AppPropsWithLayout) => {
  useJsBundleInvalidation()
  usePreventNumberInputScroll()

  const router = useRouter()
  useEffect(() => {
    const handleRouteChange = (url: string) => {
      void analytics.then((analytics) => analytics.page(router.pathname, { url }))
    }

    router.events.on('routeChangeComplete', handleRouteChange)

    return () => {
      router.events.off('routeChangeComplete', handleRouteChange)
    }
  }, [router])

  const getLayout = Component.getLayout ?? ((page: ReactElement) => page)

  // DO NOT REMOVE
  if (otherProps.router.route === '/uptime') {
    return <UpTimePage />
  }

  return (
    <>
      <Head>
        <title>Omnea</title>
      </Head>
      <SessionProvider
        session={serverSession}
        // Disable built-in refresh - TokenProvider handles visibility-aware refresh
        refetchOnWindowFocus={false}
        refetchInterval={0}
      >
        <TokenProvider>
          <Auth>
            <StyledEngineProvider injectFirst>
              <ThemeProvider>
                <ReactQueryProvider>
                  <TranslationsProvider>
                    <SnackbarProvider>
                      <PageTitleProvider>
                        <AuthenticatedAppProviders>
                          <AppWrapper disableSidebar={Component.disableSidebar} pathname={otherProps.router.route}>
                            {getLayout(<Component {...pageProps} />)}
                          </AppWrapper>
                        </AuthenticatedAppProviders>
                      </PageTitleProvider>
                    </SnackbarProvider>
                  </TranslationsProvider>
                </ReactQueryProvider>
              </ThemeProvider>
            </StyledEngineProvider>
          </Auth>
        </TokenProvider>
      </SessionProvider>
    </>
  )
}

export default App
